import { memo, useCallback, useMemo, useState } from 'react';
import { resolveLevelBounds } from '@pointorama/potree-shared';
import { AnnotationContextGroup, collectLeafAnnotations } from '../../../contexts/AnnotationContext';
import { PropertiesSection, Property } from './PropertiesSection';
import { useFloorProperties } from '../../../modules/Properties/integration/annotations/FloorProperties';
import { useFloorGroupsData } from '../../../hooks/potree/useFloorGroupsData';
import { InputWithUnit } from '../../../components/inputs/Input/InputWithUnit';
import { ConvertType, useCondMeterToFeet, useCondFeetToMeter } from '../../../hooks/useFormatNumber';
import { useT } from '../../../translation/src';
import { PropertyPanel } from '../../Properties/components/PropertyPanel';
import { AnnotationType } from '../../Properties/types';
import { ChildPropertiesSection } from './ChildPropertiesSection';

interface FloorPropertiesProps {
  projectId: string;
  group: AnnotationContextGroup;
}
const FloorProperties_: React.FC2<FloorPropertiesProps> = ({ projectId, group }) => {
  // Floor elevation properties
  const { getFloorPlanePosition, setFloorPlanePosition, getCeilingPlanePosition, setCeilingPlanePosition } = useFloorProperties(projectId);

  const { condMeterToFeet } = useCondMeterToFeet();
  const { condFeetToMeter } = useCondFeetToMeter();

  // Translations
  const information = useT('information', { swc: true });
  const type = useT('type', { swc: true });
  const floor = useT('floor', { swc: true });
  const numberOfRooms = useT('number of rooms', { swc: true });
  const dimensions = useT('Dimensions', { swc: true });
  const floorElevationLabel = useT('floor elevation', { swc: true });
  const ceilingElevationLabel = useT('ceiling elevation', { swc: true });
  const heightLabel = useT('interior height', { swc: true });
  const slabThicknessLabel = useT('slab thickness', { swc: true });

  // Get current elevations
  const floorPosition = getFloorPlanePosition(group.identifier);
  const ceilingPosition = getCeilingPlanePosition(group.identifier);

  // Derived slab thickness (gap between this level's true ceiling and the next
  // floor). Null on legacy-contiguous data, the top level, or unset ceilings.
  const { loadLevelPlanes } = useFloorGroupsData({ id: projectId });
  const slabThickness = useMemo(() => resolveLevelBounds(loadLevelPlanes(), group.identifier)?.slabThickness ?? null, [loadLevelPlanes, group.identifier]);

  // Store original values
  const originalFloorElevation = floorPosition ? condMeterToFeet(floorPosition.z).toFixed(2) : '0.00';
  const originalCeilingElevation = ceilingPosition ? condMeterToFeet(ceilingPosition.z).toFixed(2) : '0.00';
  const originalHeight = (Number(originalCeilingElevation) - Number(originalFloorElevation)).toFixed(2);

  // Local state for input values
  const [floorElevation, setFloorElevation] = useState(originalFloorElevation);
  const [ceilingElevation, setCeilingElevation] = useState(originalCeilingElevation);
  const [height, setHeight] = useState(originalHeight);

  // Sync local state when the properties change from outside (other user,
  // undo, tools), but don't clobber the user's in-progress text with a
  // numerically-equal echo of their own edit (e.g. typing "1.5" saves and
  // comes back formatted as "1.50"). State is adjusted during render — the
  // pattern react-hooks/set-state-in-effect recommends over effects.
  const [prevFloorElevation, setPrevFloorElevation] = useState(originalFloorElevation);
  if (prevFloorElevation !== originalFloorElevation) {
    setPrevFloorElevation(originalFloorElevation);
    if (Number(floorElevation) !== Number(originalFloorElevation)) {
      setFloorElevation(originalFloorElevation);
    }
  }

  const [prevCeilingElevation, setPrevCeilingElevation] = useState(originalCeilingElevation);
  if (prevCeilingElevation !== originalCeilingElevation) {
    setPrevCeilingElevation(originalCeilingElevation);
    if (Number(ceilingElevation) !== Number(originalCeilingElevation)) {
      setCeilingElevation(originalCeilingElevation);
    }
  }

  const [prevHeight, setPrevHeight] = useState(originalHeight);
  if (prevHeight !== originalHeight) {
    setPrevHeight(originalHeight);
    if (Number(height) !== Number(originalHeight)) {
      setHeight(originalHeight);
    }
  }

  // Save handlers — dimension edits apply immediately: the plane properties
  // are reactive, so the 3D clipping and outline follow live. Incomplete
  // input ("", "-") is kept locally and only saved once it parses.
  const saveFloorElevation = useCallback(
    (value: string) => {
      const parsed = Number(value);
      if (value.trim() === '' || !Number.isFinite(parsed)) return;
      setFloorPlanePosition(group.identifier, {
        ...(floorPosition || { x: 0, y: 0 }),
        z: condFeetToMeter(parsed),
      });
    },
    [group.identifier, floorPosition, setFloorPlanePosition, condFeetToMeter],
  );

  const saveCeilingElevation = useCallback(
    (value: string) => {
      const parsed = Number(value);
      if (value.trim() === '' || !Number.isFinite(parsed)) return;
      setCeilingPlanePosition(group.identifier, {
        ...(ceilingPosition || { x: 0, y: 0 }),
        z: condFeetToMeter(parsed),
      });
    },
    [group.identifier, ceilingPosition, setCeilingPlanePosition, condFeetToMeter],
  );

  const handleFloorElevationChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newFloor = event.target.value;
      setFloorElevation(newFloor);
      // Update height when floor changes
      const newHeight = (Number(ceilingElevation) - Number(newFloor)).toFixed(2);
      setHeight(newHeight);
      saveFloorElevation(newFloor);
    },
    [ceilingElevation, saveFloorElevation],
  );

  const handleCeilingElevationChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newCeiling = event.target.value;
      setCeilingElevation(newCeiling);
      // Update height when ceiling changes
      const newHeight = (Number(newCeiling) - Number(floorElevation)).toFixed(2);
      setHeight(newHeight);
      saveCeilingElevation(newCeiling);
    },
    [floorElevation, saveCeilingElevation],
  );

  // Height edits move the ceiling to floor + height
  const handleHeightChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newHeight = event.target.value;
      setHeight(newHeight);
      const newCeiling = (Number(floorElevation) + Number(newHeight)).toFixed(2);
      setCeilingElevation(newCeiling);
      saveCeilingElevation(newCeiling);
    },
    [floorElevation, saveCeilingElevation],
  );

  // Leaves only: nested room FOLDERS share the 'ROOM' type string with room
  // annotations and must not be counted or fed to the child properties.
  const leafAnnotations = useMemo(() => collectLeafAnnotations(group), [group]);

  return (
    <div className="flex flex-col space-y-4 divide-y divide-gray-800">
      <PropertiesSection title={information}>
        <Property title={type}>{floor}</Property>
        <Property title={numberOfRooms}>{leafAnnotations.filter((annotation) => annotation.type === AnnotationType.ROOM).length}</Property>
      </PropertiesSection>
      <PropertiesSection title={dimensions}>
        <Property title={floorElevationLabel}>
          <InputWithUnit id="floor-elevation" value={floorElevation} onChange={handleFloorElevationChange} step={0.01} type="number" unit={ConvertType.DISTANCE} />
        </Property>
        <Property title={ceilingElevationLabel}>
          <InputWithUnit id="ceiling-elevation" value={ceilingElevation} onChange={handleCeilingElevationChange} step={0.01} type="number" unit={ConvertType.DISTANCE} />
        </Property>
        <Property title={heightLabel}>
          <InputWithUnit id="height" value={height} onChange={handleHeightChange} step={0.01} type="number" unit={ConvertType.DISTANCE} />
        </Property>
        {slabThickness !== null && <Property title={slabThicknessLabel}>{condMeterToFeet(slabThickness).toFixed(2)}</Property>}
      </PropertiesSection>
      <PropertyPanel projectId={projectId} annotationTypes={[AnnotationType.FLOOR]} objectIdentifiers={[group.identifier]} />
      <ChildPropertiesSection key={group.identifier} projectId={projectId} annotations={leafAnnotations} />
    </div>
  );
};

export const FloorProperties = memo(FloorProperties_);
